import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAccessToken } from '../../../store/selector/auth.selector';
import { useAppSelector } from '../../../store/store';
import noProjects from '../../../assets/images/Blank canvas.gif';
import {
    useLazyGetTemplateJSONQuery,
    useSetActiveTemplateJSONMutation,
} from '../../../store/api/chat.api';
import { useLazyGetCampaignAdsQuery } from '../../../store/api/campaign-ads.api';
import { ICampaignAdDetails, ICampaignAds } from '../../../interfaces/campaign-ads.interface';
import { CAMPAIGN_AD_GROUP } from '../../../constants/campaign-ads';
import { getCurrentlyWorkingSession } from '../../../store/selector/session.selector';
import { CampaignTab } from '../../../constants/common';
import {
    SetEditorTemplateData,
    SetRefetchCampaignAds,
    SetTemplateLoadingState,
} from '../../../store/slice/editor.slice';
import { IAdFromCreateTab } from '../../../interfaces/common.interface';
import {
    displayErrorToast,
    displaySuccessToast,
    displayWarningToast,
} from '../../../utils/ToastUtils';
import { ZipDownloadImage } from '../../../interfaces/download.interface';
import { donwloadImagesZip } from '../../../utils/DownloadUtils';
import { IGenerateCampaignRequestPayload } from '../../../pages/Apprentice/apprentice.interface';
import config from '../../../config';
import { Button, Card, Col, Dropdown, Form, InputGroup, Row, Spinner } from 'react-bootstrap';
import { getAds, getCampaignAdsSessionKey } from '../../../store/selector/campaign-ads.selector';
import { SetCampaignAds } from '../../../store/slice/campaign-ads.slice';
import { LOADING_MESSAGES } from '../../../pages/Apprentice/constants';
import CampaignAdsLoading from './CampaignAdsLoading';
import { getSelectedCampaign } from '../../../store/selector/campaign.selector';
import { getActiveBrand } from '../../../store/selector/brand.selector';
import { getOrganizationId } from '../../../store/selector/organization.selector';
import { useGenerateCampaignMutation } from '../../../store/api/apprentice.api';
import { useMarkUserOldMutation } from '../../../store/api/user.api';
import { SetUserOld } from '../../../store/slice/user.slice';
import {
    getAdFromUrlActive,
    getAdFromUrlGenerating,
} from '../../../store/selector/ad-from-url.selector';
import {
    SetAdFromUrlFlowInactive,
    SetAdFromUrlGenerating,
} from '../../../store/slice/ad-from-url.slice';
import { getRefetchCampaignAds } from '../../../store/selector/editor.selector';

interface IProps {
    store: any;
    activeTab: CampaignTab;
    setActiveTab: React.Dispatch<React.SetStateAction<CampaignTab>>;
    generatingCampaign: boolean;
    setGeneratingCampaign: React.Dispatch<React.SetStateAction<boolean>>;
    setIsEditorOpen: React.Dispatch<React.SetStateAction<boolean>>;
    selectedCampaignAds: ICampaignAdDetails[];
    setSelectedCampaignAds: React.Dispatch<React.SetStateAction<ICampaignAdDetails[]>>;
    fetchCampaignAdsIntervalRef: React.MutableRefObject<NodeJS.Timeout | null>;
    campaignAdsLoading: boolean;
    setCampaignAdsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const CampaignAds = ({
    store,
    activeTab,
    setActiveTab,
    generatingCampaign,
    setGeneratingCampaign,
    setIsEditorOpen,
    selectedCampaignAds,
    fetchCampaignAdsIntervalRef,
    setSelectedCampaignAds,
    campaignAdsLoading,
    setCampaignAdsLoading,
}: IProps) => {
    const dispatch = useDispatch();
    const activeBrand = useAppSelector(getActiveBrand);
    const organizationId = useAppSelector(getOrganizationId);
    const selectedCampaign = useAppSelector(getSelectedCampaign);
    const [setActiveTemplateJSON] = useSetActiveTemplateJSONMutation();
    const [getCampaignAds] = useLazyGetCampaignAdsQuery();
    const [fetchTemplateJSONById] = useLazyGetTemplateJSONQuery();
    const adFromUrlActive = useAppSelector(getAdFromUrlActive);
    const refetchCampaignAds = useAppSelector(getRefetchCampaignAds);
    const adFromUrlGenerating = useAppSelector(getAdFromUrlGenerating);
    const currentlyWorkingSession = useAppSelector(getCurrentlyWorkingSession);
    const ads = useAppSelector(getAds);
    const adsSessionKey = useAppSelector(getCampaignAdsSessionKey);
    const [generateCampaign] = useGenerateCampaignMutation();
    const [markUserOld] = useMarkUserOldMutation();

    const [actionsLoading, setActionsLoading] = useState(false);
    const [groupBy, setGroupBy] = useState(CAMPAIGN_AD_GROUP.DELIVERABLES);
    const [isRefetching, setIsRefetching] = useState(false);
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

    const fetchCampaignsAds = async () => {
        if (!currentlyWorkingSession) return;
        setCampaignAdsLoading(true);
        try {
            const { data } = await getCampaignAds({
                sessionId: currentlyWorkingSession.sessionKey,
                groupBy,
            });
            const campaign_ads: ICampaignAds[] = data.campaign_ads;
            dispatch(
                SetCampaignAds({
                    ads: campaign_ads,
                    sessionKey: currentlyWorkingSession.sessionKey,
                }),
            );
            setCampaignAdsLoading(false);
        } catch (error) {
            dispatch(SetCampaignAds({ ads: [], sessionKey: null }));
            setCampaignAdsLoading(false);
        }
    };

    const handleSelect = (toBeSelectedAd: ICampaignAdDetails) => {
        const oldSelectedCampaignAds = [...selectedCampaignAds];

        const index = oldSelectedCampaignAds.findIndex(
            (ad) => ad.template === toBeSelectedAd.template,
        );
        if (index === -1) {
            oldSelectedCampaignAds.push(toBeSelectedAd);
        } else {
            oldSelectedCampaignAds.splice(index, 1);
        }
        setSelectedCampaignAds(oldSelectedCampaignAds);
    };

    const handleRefetchClick = async () => {
        setIsRefetching(true);
        await fetchCampaignsAds();
        setIsRefetching(false);
    };

    const handleOpenInEditor = async (ad: ICampaignAdDetails) => {
        if (!currentlyWorkingSession) return;
        const templateId = ad.template;
        const campaignAdId = ad.id;
        const caption = ad.caption_text;
        setActiveTemplateJSON({
            templateId: templateId,
            sessionId: currentlyWorkingSession.sessionKey,
        });

        dispatch(SetTemplateLoadingState(true));
        setIsEditorOpen(true);

        try {
            const templateResponse = await fetchTemplateJSONById({
                templateId: templateId,
                sessionId: currentlyWorkingSession.sessionKey,
            });
            const templateJSON = (templateResponse as any).data.template;
            store.loadJSON(templateJSON);

            const newAdFromCreateTab: IAdFromCreateTab = {
                campaignAdId: campaignAdId,
                templateId: templateId,
                caption: caption,
                templateJSON: templateJSON,
            };
            dispatch(
                SetEditorTemplateData({
                    adFromChat: null,
                    adFromCreateTab: newAdFromCreateTab,
                    templateFromProject: null,
                }),
            );
            dispatch(SetTemplateLoadingState(false));
        } catch (error) {
            displayErrorToast('Failed to open the ad. Please refresh the page');
            dispatch(SetTemplateLoadingState(false));
            setIsEditorOpen(false);
        }
    };

    const downloadImages = async () => {
        setActionsLoading(true);
        const images: ZipDownloadImage[] = [];
        let index = 1;
        selectedCampaignAds.forEach((selectedCampaignAd) => {
            images.push({
                url: selectedCampaignAd.preview,
                filename: `ad_${index++}.png`,
            });
        });
        await donwloadImagesZip('campaign_projects', images);
        setActionsLoading(false);
    };

    const handlePublish = () => {
        if (selectedCampaignAds.length === 0) {
            displayWarningToast('Select ads to publish');
            return;
        }
        setActiveTab(CampaignTab.PUBLISH);
    };

    const getDisplayGroupBy = (group: CAMPAIGN_AD_GROUP) => {
        if (group === CAMPAIGN_AD_GROUP.DELIVERABLES) {
            return 'Deliverables';
        }
        if (group === CAMPAIGN_AD_GROUP.PLATFORM) {
            return 'Platform';
        }
        if (group === CAMPAIGN_AD_GROUP.ASSET_TYPE) {
            return 'Asset Type';
        }
    };

    const handleSelectGroupBy = (value: CAMPAIGN_AD_GROUP) => {
        if (!campaignAdsLoading) {
            setGroupBy(value);
        }
    };

    const showAdsLoading = () => {
        if (currentlyWorkingSession) {
            if (ads.length > 0) {
                if (currentlyWorkingSession.sessionKey !== adsSessionKey) return true;
                return false;
            } else {
                return generatingCampaign ? true : campaignAdsLoading;
            }
        } else return false;
    };

    const generateAdFromUrlCampaign = async () => {
        if (adFromUrlGenerating) return;
        if (selectedCampaign && activeBrand && organizationId && currentlyWorkingSession) {
            dispatch(SetAdFromUrlGenerating(true));
            dispatch(SetCampaignAds({ ads: [], sessionKey: null }));

            const campaignTemplateIds: string[] = [];
            const campaignUserProductPaths: string[] = [];
            const tagsList: string[][] = [];

            selectedCampaign.deliverables.forEach((deliverable) => {
                if (deliverable.tags) {
                    tagsList.push(deliverable.tags);
                } else {
                    tagsList.push([]);
                }

                if (deliverable.sub_deliverable) {
                    campaignUserProductPaths.push('');
                    campaignTemplateIds.push('');
                } else {
                    if (deliverable.user_product_path && deliverable.user_product_path.length > 0) {
                        campaignUserProductPaths.push(deliverable.user_product_path);
                    } else campaignUserProductPaths.push('');

                    if (
                        deliverable.templateDetails &&
                        deliverable.templateDetails.preview_url.length > 0
                    ) {
                        campaignTemplateIds.push(deliverable.templateDetails.id);
                    } else campaignTemplateIds.push('');
                }
            });

            const generateCampaignRequestPayload: IGenerateCampaignRequestPayload = {
                prompt: 'Approve and Generate',
                sessionId: currentlyWorkingSession.sessionKey,
                campaignId: selectedCampaign.id,
                campaignUserProductPaths: campaignUserProductPaths,
                campaignTemplateNames: campaignTemplateIds,
                brandId: activeBrand.id,
                organizationId: organizationId,
                tags: tagsList,
            };

            try {
                const campaignResponse: any = await generateCampaign(
                    generateCampaignRequestPayload,
                );
                const success = campaignResponse && campaignResponse.data;
                setGeneratingCampaign(false);
                if (!success) {
                    displayErrorToast("Couldn't generate campaign. Please try again.");
                } else {
                    displaySuccessToast('Your campaign is ready');
                    await markUserOld();
                    dispatch(SetUserOld());
                }
                dispatch(SetAdFromUrlFlowInactive());
            } catch (e) {
                dispatch(SetAdFromUrlFlowInactive());
                displayErrorToast("Couldn't generate campaign. Please try again.");
            }
        }
    };

    useEffect(() => {
        if (generatingCampaign) {
            if (!campaignAdsLoading && !fetchCampaignAdsIntervalRef.current) {
                fetchCampaignsAds();
            }

            if (!fetchCampaignAdsIntervalRef.current) {
                fetchCampaignAdsIntervalRef.current = setInterval(() => {
                    if (!campaignAdsLoading) {
                        fetchCampaignsAds();
                    }
                }, 5000);
            }
        } else if (
            !campaignAdsLoading &&
            (ads.length === 0 ||
                (currentlyWorkingSession && currentlyWorkingSession.sessionKey !== adsSessionKey))
        ) {
            fetchCampaignsAds();
        }

        if (!generatingCampaign && fetchCampaignAdsIntervalRef.current) {
            clearInterval(fetchCampaignAdsIntervalRef.current);
            fetchCampaignAdsIntervalRef.current = null;
        }

        return () => {
            if (fetchCampaignAdsIntervalRef.current) {
                clearInterval(fetchCampaignAdsIntervalRef.current);
                fetchCampaignAdsIntervalRef.current = null;
            }
        };
    }, [generatingCampaign, activeTab, currentlyWorkingSession, groupBy]);

    useEffect(() => {
        if (generatingCampaign) {
            const interval = setInterval(() => {
                setLoadingMessageIndex((prevIndex) =>
                    prevIndex === LOADING_MESSAGES.length - 1 ? 0 : prevIndex + 1,
                );
            }, 4000);

            return () => clearInterval(interval);
        }
    }, []);

    useEffect(() => {
        if (refetchCampaignAds) {
            dispatch(SetRefetchCampaignAds(false));
            handleRefetchClick();
        }
    }, [refetchCampaignAds]);

    // useEffect(() => {
    //     if (adFromUrlActive && selectedCampaign) {
    //         generateAdFromUrlCampaign();
    //     }
    // }, [selectedCampaign, adFromUrlActive]);

    return (
        <div className="tw-relative tw-flex tw-items-center tw-justify-center tw-flex-col tw-mt-6">
            {generatingCampaign ? (
                <div className="tw-w-full tw-flex tw-justify-start">
                    <div className="tw-flex tw-items-center tw-justify-start tw-gap-x-[6px] tw-bg-[#ECE3EE] tw-py-[6px] tw-px-2 tw-rounded-md tw-text-purple-500">
                        <Spinner className="tw-w-6 tw-h-6" />
                        <div className="tw-font-semibold tw-text-sm">
                            {LOADING_MESSAGES[loadingMessageIndex]} ...
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {ads.length > 0 &&
                        currentlyWorkingSession &&
                        currentlyWorkingSession.sessionKey === adsSessionKey && (
                            <div className="tw-w-full tw-sticky tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-z-[80] tw-bg-white tw-shadow-md tw-px-6 tw-py-2">
                                <div className="tw-flex tw-items-center tw-justify-between">
                                    <div className="tw-w-full tw-flex tw-items-center tw-justify-between">
                                        {selectedCampaignAds.length > 0 ? (
                                            <div className="tw-flex tw-items-center tw-gap-x-1 tw-text-base">
                                                <span className="tw-font-semibold">Selected:</span>
                                                <span>{selectedCampaignAds.length}</span>
                                            </div>
                                        ) : (
                                            <div className="tw-flex tw-items-center tw-gap-x-1 tw-text-base">
                                                <span className="tw-font-semibold">
                                                    Mark the ones you want to download or schedule
                                                </span>
                                            </div>
                                        )}
                                        <div className="tw-flex tw-items-center tw-gap-x-2">
                                            <Button variant="primary" onClick={handlePublish}>
                                                Publish
                                            </Button>
                                            {selectedCampaignAds.length > 0 && (
                                                <Button
                                                    variant="outline-primary"
                                                    // className="iconbtn rounded-circle"
                                                    onClick={downloadImages}
                                                >
                                                    Download
                                                    {actionsLoading && (
                                                        <Spinner className="tw-w-6 tw-h-6" />
                                                    )}
                                                </Button>
                                            )}

                                            <InputGroup className="custom-dropdown tw-w-fit">
                                                <Dropdown>
                                                    <Dropdown.Toggle
                                                        variant="outline-primary"
                                                        id="dropdown-basic"
                                                        className="dropdown__toggle"
                                                    >
                                                        <i className="ph ph-funnel-simple md"></i>{' '}
                                                        {getDisplayGroupBy(groupBy)}
                                                    </Dropdown.Toggle>

                                                    <Dropdown.Menu className="dropdown__toggle--menu">
                                                        <Dropdown.Item
                                                            onClick={() => {
                                                                handleSelectGroupBy(
                                                                    CAMPAIGN_AD_GROUP.DELIVERABLES,
                                                                );
                                                            }}
                                                        >
                                                            {getDisplayGroupBy(
                                                                CAMPAIGN_AD_GROUP.DELIVERABLES,
                                                            )}
                                                        </Dropdown.Item>
                                                        <Dropdown.Item
                                                            onClick={() => {
                                                                handleSelectGroupBy(
                                                                    CAMPAIGN_AD_GROUP.PLATFORM,
                                                                );
                                                            }}
                                                        >
                                                            {getDisplayGroupBy(
                                                                CAMPAIGN_AD_GROUP.PLATFORM,
                                                            )}
                                                        </Dropdown.Item>
                                                        <Dropdown.Item
                                                            onClick={() => {
                                                                handleSelectGroupBy(
                                                                    CAMPAIGN_AD_GROUP.ASSET_TYPE,
                                                                );
                                                            }}
                                                        >
                                                            {getDisplayGroupBy(
                                                                CAMPAIGN_AD_GROUP.ASSET_TYPE,
                                                            )}
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </InputGroup>

                                            <Button
                                                variant="outline-primary"
                                                className="iconbtn rounded-circle"
                                                onClick={handleRefetchClick}
                                            >
                                                {isRefetching ? (
                                                    <Spinner
                                                        style={{
                                                            width: '1.5rem',
                                                            height: '1.5rem',
                                                        }}
                                                    />
                                                ) : (
                                                    <i
                                                        className="ph ph-arrows-clockwise"
                                                        style={{ fontSize: '20px' }}
                                                    ></i>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                </>
            )}

            <div className={`tw-w-full ${generatingCampaign ? '' : ' '}`}>
                {showAdsLoading() ? (
                    <CampaignAdsLoading />
                ) : (
                    <div className="tw-w-full">
                        {ads.length === 0 ? (
                            <div className="tw-flex tw-flex-col tw-justify-center tw-items-center">
                                <img src={noProjects} />
                                <h1 className="text-dark">This campaign has no projects yet!</h1>
                            </div>
                        ) : (
                            <>
                                {ads.map((row, index) => (
                                    <div key={index}>
                                        <Row className="tw-w-full tw-flex tw-flex-col">
                                            <div className="tw-w-full tw-flex tw-items-center tw-gap-x-2 mt-3 tw-mb-0">
                                                <span className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-full tw-font-bold tw-bg-purple-500 tw-text-white tw-text-base tw-p-3">
                                                    {index + 1}
                                                </span>
                                                <div className="tw-w-full tw-flex tw-items-center tw-border tw-border-gray-200 tw-rounded-lg">
                                                    <div className="tw-p-4 tw-text-base tw-text-black">
                                                        {row.label}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="tw-w-full tw-flex tw-items-center tw-gap-x-2 mt-3 tw-mb-6">
                                                <span className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-full tw-font-bold tw-bg-white tw-text-white tw-text-base tw-p-3">
                                                    {index + 1}
                                                </span>
                                                <Row className="tw-flex flex-wrap tw-m-0 tw-p-0">
                                                    {row.ads.map((campaign_ad, index) => (
                                                        <Col
                                                            xs={12}
                                                            sm={6}
                                                            lg={4}
                                                            key={index}
                                                            className="mb-3 tw-pl-0 tw-max-w-[400px] tw-cursor-pointer"
                                                        >
                                                            <Card
                                                                className={`project-content box-shadow p-2 m-0 position-relative ${
                                                                    selectedCampaignAds.findIndex(
                                                                        (ad) =>
                                                                            ad.template ===
                                                                            campaign_ad.template,
                                                                    ) !== -1
                                                                        ? 'border-primary'
                                                                        : ''
                                                                }`}
                                                                onClick={() => {
                                                                    handleOpenInEditor(campaign_ad);
                                                                }}
                                                            >
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    className="floating-checkbox position-absolute tw-scale-150 tw-mt-[2px] tw-ml-[2px]"
                                                                    defaultChecked={
                                                                        selectedCampaignAds.findIndex(
                                                                            (ad) =>
                                                                                ad.template ===
                                                                                campaign_ad.template,
                                                                        ) !== -1
                                                                    }
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                    }}
                                                                    onChange={() =>
                                                                        handleSelect(campaign_ad)
                                                                    }
                                                                />
                                                                <Card.Img
                                                                    variant="top"
                                                                    src={campaign_ad.preview}
                                                                />
                                                            </Card>
                                                        </Col>
                                                    ))}
                                                </Row>
                                            </div>
                                        </Row>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CampaignAds;
